import { useState, type FC } from "react";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import { CommentIcon } from "../../atoms/icons/CommentIcon";
import Modal from "../Modal";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Alert from "../Alert";
import MultilineTextField from "../../atoms/inputs/MultilineTextField";
import ActionButton from "../../atoms/buttons/ActionButton";
import { useCreateComment } from "../../../graphql/hooks/useCreateComment";
import type { InvoiceDocumentType } from "../../../models/InvoiceDocumentType";
import Checkbox from "../../atoms/inputs/Checkbox";
import { useTranslation } from "react-i18next";

interface Props {
  clientIdentifier?: string;
  documents?: Array<InvoiceDocumentType>;
}

const LeaveInfoButton: FC<Props> = ({ clientIdentifier, documents }) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [infoComment, setInfoComment] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [selectedDocuments, setSelectedDocuments] = useState<Array<string>>([]);

  const onSuccessLeaveComment = (result?: any) => {
    if (result?.createComment?.success === true) {
      setInfoComment("");
      setSuccessMessage(t("documentHandler.yourCommentHasBeenSaved"));
      setErrorMessage("");
      setSelectedDocuments([]);
    } else {
      setSuccessMessage("");
      setErrorMessage(t("documentHandler.commentCouldNotBeSaved"));
    }
  };
  const onErrorLeaveComment = (e?: any) => {
    setErrorMessage(
      t("documentHandler.commentCouldNotBeSaved" + (e?.toString() || "")),
    );
  };
  const { createComment, error, loading } = useCreateComment({
    onCompleted: onSuccessLeaveComment,
    onError: onErrorLeaveComment,
  });

  const onClickLeaveComment = async () => {
    for (const docId of selectedDocuments) {
      try {
        createComment({
          variables: {
            input: {
              clientIdentifier: clientIdentifier,
              content: infoComment,
              parentId: docId,
              parentType: "INVOICE_UPLOAD_UNIT_DOCUMENT",
            },
          },
        });
      } catch (error) {
        setErrorMessage(
          t("documentHandler.commentCouldNotBeSaved") + error?.toString(),
        );
      }
    }
  };

  const onToggleDocument = (documentId: string) => {
    if (selectedDocuments.includes(documentId)) {
      const filtered = selectedDocuments.filter((x) => x !== documentId);
      setSelectedDocuments(filtered);
    } else {
      setSelectedDocuments([...selectedDocuments, documentId]);
    }
  };

  return (
    <div>
      <DefaultButton
        title={`${t("documentHandler.leaveInfo")}`}
        icon={<CommentIcon />}
        onClick={() => setModalOpen(true)}
        size="small"
      />

      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          closeIcon
          width={400}
          title={t("documentHandler.leaveInfo")}
        >
          {documents?.map((document) => (
            <div>
              <Checkbox
                title={document.file.fileName}
                checked={selectedDocuments.includes(document.id)}
                onChange={() => onToggleDocument(document.id)}
              />
            </div>
          ))}
          <HorizontalSpacer spacing={2} />

          <MultilineTextField
            label={t("common.comment")}
            placeholder={t("documentHandler.enterComment")}
            onChange={(value) => setInfoComment(value)}
            value={infoComment}
          />
          <HorizontalSpacer spacing={1} />
          <ActionButton
            title={t("documentHandler.leaveInfo")}
            color={theme.colors.primary}
            icon={<CommentIcon color={theme.colors.white} />}
            loading={loading}
            disabled={!infoComment || selectedDocuments.length < 1}
            onClick={onClickLeaveComment}
          />

          <HorizontalSpacer spacing={2} />
          {successMessage && <Alert type="success" title={successMessage} />}
          {errorMessage && (
            <Alert
              type="error"
              title={errorMessage}
              message={error?.toString()}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

export default LeaveInfoButton;
